import { BaseButton } from '@/components/common/base-button';
import { ArrowFloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ParkingSlotButton } from '@/components/feature/parking/horizontal/parking-slot-button';
import type { ParkingSlot, ParkingSlotListHandle } from '@/components/feature/parking/types';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CurrentLocationIcon } from '@/components/icons';
import { KEY_CODE_PAJU } from '@/constants/app';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useParkingSlideManagement } from '@/hooks/parking';
import { useConfigStore } from '@/stores';
import clsx from 'clsx';
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import styles from './styles.module.scss';

type Props = {
    slots: ParkingSlot[];
    selectedSlotId?: string;
    onSelectSlot?: (slot: ParkingSlot) => void;
    onShowMyLocation?: () => void;
    variant?: 'horizontal' | 'vertical' | 'sidebar';
};

const ParkingSlotListInner = forwardRef<ParkingSlotListHandle, Props>(
    ({ slots, selectedSlotId, onSelectSlot, onShowMyLocation, variant = 'horizontal' }, ref) => {
        const { t } = useTranslation();
        const { speak } = useAccessibilityTTS();
        const { scaleHeightPixel } = useScreenSize();
        const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
        const isPaju = KEY_CODE_PAJU.includes(storeCode);
        const buttonText = t('common.button.tts.button');
        const inactiveButtonText = t('common.button.tts.inactive_button');
        const isVertical = variant === 'vertical';
        const isSidebar = variant === 'sidebar';

        const regionOrder = useMemo(
            () => ({
                slot: isSidebar ? 3 : isVertical ? 6 : 3,
                prev: isSidebar ? 3 : isVertical ? 7 : 4,
                next: isSidebar ? 3 : isVertical ? 8 : 5,
                myLocation: isSidebar ? 2 : isVertical ? 9 : 6,
            }),
            [isVertical, isSidebar]
        );

        const tabIndexOrder = useMemo(
            () => ({
                slot: isSidebar ? 3 : 7,
                prev: isSidebar ? 3 : 8,
                next: isSidebar ? 3 : 9,
                myLocation: isSidebar ? 2 : 10,
            }),
            [isSidebar]
        );

        const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);
        const speakCleanupRef = useRef<(() => void) | null>(null);
        const swiperContainerRef = useRef<HTMLDivElement>(null);

        const cancelPendingSpeak = useCallback(() => {
            speakCleanupRef.current?.();
            speakCleanupRef.current = null;
        }, []);

        const scheduleSpeak = useCallback(
            (text: string) => {
                cancelPendingSpeak();
                speakCleanupRef.current = speak(text) ?? null;
            },
            [cancelPendingSpeak, speak]
        );

        const { swiperRef, isDisabledPrev, isDisabledNext, handleSlidePrev, handleSlideNext, updateNavigationState, resetSlidePosition } =
            useParkingSlideManagement();

        useImperativeHandle(ref, () => ({ resetSlidePosition }), [resetSlidePosition]);

        const handlePrevClick = useCallback(() => {
            if (isDisabledPrev) {
                speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
            } else {
                speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
                handleSlidePrev();
            }
        }, [isDisabledPrev, speak, t, handleSlidePrev]);

        const handlePrevFocus = useCallback(() => {
            if (isDisabledPrev) {
                speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
            } else {
                speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
            }
        }, [isDisabledPrev, speak, t, buttonText, inactiveButtonText]);

        const handleNextClick = useCallback(() => {
            if (isDisabledNext) {
                speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
            } else {
                speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
                handleSlideNext();
            }
        }, [isDisabledNext, speak, t, handleSlideNext]);

        const handleNextFocus = useCallback(() => {
            if (isDisabledNext) {
                speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
            } else {
                speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
            }
        }, [isDisabledNext, speak, t, buttonText, inactiveButtonText]);

        const handleMyLocationFocus = () => {
            if (!lastInteractionWasMouse) {
                scheduleSpeak(`${t('floor_guide.button.current_location')}, ${buttonText}`);
            }
            setLastInteractionWasMouse(false);
        };

        const handleMyLocationBlur = () => {
            cancelPendingSpeak();
        };

        const handleMyLocationMouseDown = () => {
            setLastInteractionWasMouse(true);
        };

        useDisableWheelEvent(swiperContainerRef);

        const showNavButtons = !isDisabledPrev || !isDisabledNext;

        if (isSidebar) {
            return (
                <section className={styles['parking-slot-wrapper--sidebar']}>
                    <section className={styles['parking-slot__inner']}>
                        <section className={styles['parking-slot__actions--sidebar']}>
                            <SecondaryFloorButton
                                layout="horizontal"
                                data-region-nav
                                tabIndex={tabIndexOrder.myLocation}
                                data-interactive
                                data-region-order={regionOrder.myLocation}
                                data-tts-scope="my-location-button"
                                aria-label={`${t('floor_guide.button.current_location')}, ${buttonText}`}
                                onMouseDown={handleMyLocationMouseDown}
                                onFocus={handleMyLocationFocus}
                                onBlur={handleMyLocationBlur}
                                onClick={onShowMyLocation}
                            >
                                <CurrentLocationIcon />
                                {t('floor_guide.button.current_location')}
                            </SecondaryFloorButton>
                        </section>
                        <section className={styles['parking-slot__columns']} data-region-nav data-region-order={regionOrder.slot}>
                            <div className={styles['parking-slot__grid']}>
                                {slots.map((slot) => {
                                    const isSelected = selectedSlotId === slot.id;
                                    return (
                                        <ParkingSlotButton
                                            key={slot.id}
                                            variant="sidebar"
                                            data-region-order={regionOrder.slot}
                                            tabIndex={tabIndexOrder.slot}
                                            data-interactive
                                            role="radio"
                                            aria-checked={isSelected}
                                            isSelected={isSelected}
                                            isSmallFont={isPaju && slot.name.length >= 5}
                                            aria-label={slot.name}
                                            onClick={() => onSelectSlot?.(slot)}
                                        >
                                            {slot.name}
                                        </ParkingSlotButton>
                                    );
                                })}
                            </div>
                        </section>
                    </section>
                </section>
            );
        }

        return (
            <section
                className={clsx(styles['parking-slot-wrapper'], {
                    [styles['parking-slot-wrapper--vertical']]: isVertical,
                })}
            >
                <section className={styles['parking-slot__content']}>
                    <section className={styles['parking-slot__list']}>
                        {showNavButtons && (
                            <section className={styles['parking-slot__nav-btn']}>
                                <BaseButton
                                    className={styles['parking-slot__nav-btn-inner']}
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={regionOrder.prev}
                                    tabIndex={tabIndexOrder.prev}
                                    data-interactive
                                    aria-disabled={isDisabledPrev}
                                    onClick={handlePrevClick}
                                    onFocus={handlePrevFocus}
                                >
                                    <ChevronLeftIcon />
                                </BaseButton>
                            </section>
                        )}

                        <div ref={swiperContainerRef} className="min-w-0 flex-1 overflow-clip" data-region-nav data-region-order={regionOrder.slot}>
                            <Swiper
                                className={clsx(styles['parking-slot__list-container'], 'scrollbar-hide')}
                                modules={[FreeMode]}
                                mousewheel={false}
                                allowTouchMove={true}
                                simulateTouch={true}
                                direction="horizontal"
                                spaceBetween={0}
                                slidesPerView="auto"
                                freeMode={{
                                    enabled: true,
                                    momentum: true,
                                    momentumRatio: 0.8,
                                }}
                                loop={false}
                                ref={swiperRef}
                                preventInteractionOnTransition
                                onAfterInit={() => updateNavigationState()}
                                onSlideChange={() => updateNavigationState()}
                                onReachBeginning={() => updateNavigationState()}
                                onReachEnd={() => updateNavigationState()}
                                onFromEdge={() => updateNavigationState()}
                            >
                                {slots.map((slot) => {
                                    const isSelected = selectedSlotId === slot.id;
                                    return (
                                        <SwiperSlide key={slot.id} className={styles['parking-slot__slide']}>
                                            <ParkingSlotButton
                                                data-region-order={regionOrder.slot}
                                                tabIndex={tabIndexOrder.slot}
                                                data-interactive
                                                role="radio"
                                                aria-checked={isSelected}
                                                isSelected={isSelected}
                                                isSmallFont={isPaju && slot.name.length >= 5}
                                                aria-label={slot.name}
                                                onClick={() => onSelectSlot?.(slot)}
                                            >
                                                {slot.name}
                                            </ParkingSlotButton>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>

                        {showNavButtons && (
                            <section className={styles['parking-slot__nav-btn']}>
                                <BaseButton
                                    className={styles['parking-slot__nav-btn-inner']}
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={regionOrder.next}
                                    tabIndex={tabIndexOrder.next}
                                    data-interactive
                                    aria-disabled={isDisabledNext}
                                    onClick={handleNextClick}
                                    onFocus={handleNextFocus}
                                >
                                    <ChevronRightIcon />
                                </BaseButton>
                            </section>
                        )}
                    </section>
                    <section className={styles['parking-slot__actions']}>
                        <SecondaryFloorButton
                            data-region-nav
                            tabIndex={tabIndexOrder.myLocation}
                            data-interactive
                            data-region-order={regionOrder.myLocation}
                            data-tts-scope="my-location-button"
                            aria-label={`${t('floor_guide.button.current_location')}, ${buttonText}`}
                            onMouseDown={handleMyLocationMouseDown}
                            onFocus={handleMyLocationFocus}
                            onBlur={handleMyLocationBlur}
                            onClick={onShowMyLocation}
                            className={
                                isVertical
                                    ? undefined
                                    : '!h-[calc(52*var(--vh-unit))] !w-[calc(172*var(--vw-unit))]'
                            }
                        >
                            <CurrentLocationIcon />
                            {t('floor_guide.button.current_location')}
                        </SecondaryFloorButton>
                    </section>
                </section>
            </section>
        );
    }
);

ParkingSlotListInner.displayName = 'ParkingSlotList';

export const ParkingSlotList = memo(ParkingSlotListInner);
